import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Send, Image, MoreHorizontal, Trash2, Bug } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { TypingIndicator } from '../components/messages/TypingIndicator';
import { OnlineStatus } from '../components/messages/OnlineStatus';
import { MessageStatus } from '../components/messages/MessageStatus';
import { RealtimeDebugPanel } from '../components/messages/RealtimeDebugPanel';
import { useAuthStore } from '../store/authStore';
import { 
  getConversation, 
  getMessages, 
  sendMessage, 
  deleteMessage, 
  markMessagesAsRead 
} from '../lib/messageService';
import { realtimeService } from '../lib/realtimeService';
import { Conversation, Message } from '../types';

export const ConversationPage: React.FC = () => {
  const { conversationId } = useParams<{ conversationId: string }>();
  const { user } = useAuthStore();
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [typingUsers, setTypingUsers] = useState<Array<{ id: string; username: string; avatar_url?: string }>>([]);
  const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set());
  const [messageStatuses, setMessageStatuses] = useState<Map<string, 'sending' | 'sent' | 'delivered' | 'read'>>(new Map());
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastTypingRef = useRef<number>(0);

  useEffect(() => {
    if (conversationId && user) {
      loadConversationData();
    }
  }, [conversationId, user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (conversationId && user) {
      console.log('🔄 Setting up real-time subscriptions...');
      
      // Mark messages as read when entering conversation
      markMessagesAsRead(conversationId, user.id);

      // Subscribe to real-time updates
      const unsubscribeMessages = realtimeService.subscribeToConversation(
        conversationId,
        handleNewMessage,
        handleMessageUpdate,
        handleMessageDelete
      );

      const unsubscribeTyping = realtimeService.subscribeToTyping(
        conversationId,
        handleTypingUpdate
      );

      const unsubscribePresence = realtimeService.subscribeToPresence(
        conversationId,
        user.id,
        {
          username: user.email?.split('@')[0] || 'user',
          avatar_url: user.user_metadata?.avatar_url
        },
        handlePresenceUpdate
      );

      const unsubscribeReadStatus = realtimeService.subscribeToReadStatus(
        conversationId,
        handleReadStatusUpdate
      );

      return () => {
        console.log('🔌 Cleaning up subscriptions...');
        unsubscribeMessages();
        unsubscribeTyping();
        unsubscribePresence();
        unsubscribeReadStatus();
      };
    }
  }, [conversationId, user]);

  const loadConversationData = async () => {
    if (!conversationId || !user) return;

    setLoading(true);
    try {
      console.log('📥 Loading conversation data...');
      const [conv, msgs] = await Promise.all([
        getConversation(conversationId, user.id),
        getMessages(conversationId)
      ]);

      console.log('✅ Conversation loaded:', conv);
      console.log('✅ Messages loaded:', msgs.length);

      setConversation(conv);
      setMessages(msgs);
    } catch (error) {
      console.error('❌ Error loading conversation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewMessage = (message: Message) => {
    console.log('📨 Handling new message:', message);
    
    setMessages(prev => {
      // Avoid duplicates
      if (prev.find(m => m.id === message.id)) {
        console.log('⚠️ Duplicate message detected, skipping');
        return prev;
      }
      console.log('✅ Adding new message to state');
      return [...prev, message];
    });

    // Mark as read if not from current user
    if (message.sender_id !== user?.id && conversationId) {
      markMessagesAsRead(conversationId, user?.id || '');
    }
  };

  const handleMessageUpdate = (updatedMessage: Message) => {
    console.log('📝 Handling message update:', updatedMessage);
    setMessages(prev => prev.map(msg => 
      msg.id === updatedMessage.id ? updatedMessage : msg
    ));
  };

  const handleMessageDelete = (messageId: string) => {
    console.log('🗑️ Handling message delete:', messageId);
    setMessages(prev => prev.filter(msg => msg.id !== messageId));
  };

  const handleTypingUpdate = (payload: any) => {
    console.log('⌨️ Handling typing update:', payload);
    const { user_id, is_typing, timestamp } = payload.payload || payload;
    
    if (user_id === user?.id) return; // Ignore own typing

    setTypingUsers(prev => {
      if (is_typing) {
        // Add user to typing list if not already there
        if (!prev.find(u => u.id === user_id)) {
          return [...prev, { id: user_id, username: payload.username || 'User' }];
        }
        return prev;
      } else {
        // Remove user from typing list
        return prev.filter(u => u.id !== user_id);
      }
    });

    // Auto-remove typing indicator after 3 seconds
    setTimeout(() => {
      setTypingUsers(prev => prev.filter(u => u.id !== user_id));
    }, 3000);
  };

  const handlePresenceUpdate = (payload: any) => {
    console.log('👥 Handling presence update:', payload);
    
    if (payload.event === 'join') {
      payload.presences.forEach((presence: any) => {
        setOnlineUsers(prev => new Set(prev).add(presence.user_id));
      });
    } else if (payload.event === 'leave') {
      payload.presences.forEach((presence: any) => {
        setOnlineUsers(prev => {
          const newSet = new Set(prev);
          newSet.delete(presence.user_id);
          return newSet;
        });
      });
    } else {
      // Sync event - update all online users
      const allUsers = new Set<string>();
      Object.values(payload).forEach((presences: any) => {
        presences.forEach((presence: any) => {
          allUsers.add(presence.user_id);
        });
      });
      setOnlineUsers(allUsers);
    }
  };

  const handleReadStatusUpdate = (payload: any) => {
    console.log('📖 Handling read status update:', payload);
    const { message_id, user_id } = payload.payload || payload;
    
    if (user_id !== user?.id) {
      setMessageStatuses(prev => new Map(prev).set(message_id, 'read'));
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewMessage(e.target.value);
    
    // Send typing indicator
    if (conversationId && user) {
      const now = Date.now();
      lastTypingRef.current = now;
      
      realtimeService.sendTypingIndicator(conversationId, user.id, true);
      
      // Clear previous timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Stop typing indicator after 1 second of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        if (Date.now() - lastTypingRef.current >= 1000) {
          realtimeService.sendTypingIndicator(conversationId, user.id, false);
        }
      }, 1000);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!conversationId || !user || !newMessage.trim() || sending) return;

    const messageContent = newMessage.trim();
    const tempId = `temp-${Date.now()}`;
    
    console.log('📤 Sending message:', messageContent);
    
    // Optimistic update
    const tempMessage: Message = {
      id: tempId,
      conversation_id: conversationId,
      sender_id: user.id,
      sender: {
        id: user.id,
        username: user.email?.split('@')[0] || 'user',
        displayName: user.user_metadata?.full_name || user.email?.split('@')[0] || 'User',
        profileImage: user.user_metadata?.avatar_url || 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=300',
        bio: '',
        followerCount: 0,
        followingCount: 0,
        postCount: 0,
      },
      content: messageContent,
      message_type: 'text',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      is_read: false,
      is_deleted: false,
    };

    setMessages(prev => [...prev, tempMessage]);
    setNewMessage('');
    setMessageStatuses(prev => new Map(prev).set(tempId, 'sending'));
    
    // Stop typing indicator
    realtimeService.sendTypingIndicator(conversationId, user.id, false);

    setSending(true);
    try {
      await sendMessage(conversationId, user.id, messageContent);
      console.log('✅ Message sent successfully');
      
      // Remove temp message and update status
      setMessages(prev => prev.filter(msg => msg.id !== tempId));
      setMessageStatuses(prev => {
        const newMap = new Map(prev);
        newMap.delete(tempId);
        return newMap;
      });
    } catch (error) {
      console.error('❌ Error sending message:', error);
      // Remove temp message on error
      setMessages(prev => prev.filter(msg => msg.id !== tempId));
      setMessageStatuses(prev => {
        const newMap = new Map(prev);
        newMap.delete(tempId);
        return newMap;
      });
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    if (!user) return;

    try {
      await deleteMessage(messageId, user.id);
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Failed to delete message. Please try again.');
    }
  };

  const formatMessageTime = (dateString: string) => {
    try {
      if (!dateString || dateString === 'null' || dateString === 'undefined') {
        return 'now';
      }

      const date = new Date(dateString);
      
      if (isNaN(date.getTime())) {
        console.warn('Invalid date string in formatMessageTime:', dateString);
        return 'now';
      }

      const now = new Date();
      const diffInMilliseconds = now.getTime() - date.getTime();
      
      if (diffInMilliseconds < 0) {
        return 'now';
      }

      const diffInMinutes = Math.floor(diffInMilliseconds / (1000 * 60));
      const diffInHours = Math.floor(diffInMilliseconds / (1000 * 60 * 60));
      const diffInDays = Math.floor(diffInHours / 24);

      if (diffInMinutes < 1) {
        return 'now';
      }
      
      if (diffInMinutes < 60) {
        return `${diffInMinutes}m ago`;
      }
      
      if (diffInHours < 24) {
        return date.toLocaleTimeString([], { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true 
        });
      } else if (diffInDays < 7) {
        return date.toLocaleDateString([], { 
          weekday: 'short', 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true 
        });
      } else {
        return date.toLocaleDateString([], { 
          month: 'short', 
          day: 'numeric',
          year: diffInDays > 365 ? 'numeric' : undefined
        });
      }
    } catch (error) {
      console.error('Error formatting message time:', error, 'for date:', dateString);
      return 'now';
    }
  };

  const getOtherParticipant = () => {
    return conversation?.participants.find(p => p.id !== user?.id);
  };

  if (loading) {
    return (
      <div className="pt-16 pb-16 md:pt-20 md:pb-0 bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4 max-w-screen-md">
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="pt-16 pb-16 md:pt-20 md:pb-0 bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4 max-w-screen-md">
          <div className="text-center py-12">
            <p className="text-gray-600">Conversation not found</p>
            <Link to="/messages" className="text-purple-600 hover:text-purple-700 mt-2 inline-block">
              Back to Messages
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const otherParticipant = getOtherParticipant();

  return (
    <div className="pt-16 pb-16 md:pt-20 md:pb-0 bg-gray-50 min-h-screen flex flex-col">
      <div className="container mx-auto px-4 max-w-screen-md flex-1 flex flex-col">
        <div className="bg-white border border-gray-200 rounded-md overflow-hidden flex-1 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
            <div className="flex items-center">
              <Link to="/messages" className="text-gray-600 mr-4">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              {otherParticipant && (
                <Link to={`/profile/${otherParticipant.username}`} className="flex items-center">
                  <div className="relative">
                    <img
                      src={otherParticipant.profileImage}
                      alt={otherParticipant.username}
                      className="h-8 w-8 rounded-full object-cover mr-3"
                    />
                    {/* Online indicator */}
                    {onlineUsers.has(otherParticipant.id) && (
                      <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></div>
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{otherParticipant.username}</p>
                    <OnlineStatus 
                      isOnline={onlineUsers.has(otherParticipant.id)}
                      className="text-xs"
                    />
                  </div>
                </Link>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowDebugPanel(!showDebugPanel)}
                title="Debug Panel"
              >
                <Bug className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Messages */}
          <div 
            ref={messagesContainerRef}
            className="flex-1 overflow-y-auto p-4 space-y-4"
            style={{ maxHeight: 'calc(100vh - 200px)' }}
          >
            {messages.length > 0 ? (
              messages.map((message, index) => {
                const isOwnMessage = message.sender_id === user?.id;
                const showAvatar = !isOwnMessage && (
                  index === 0 || 
                  messages[index - 1].sender_id !== message.sender_id
                );
                const messageStatus = messageStatuses.get(message.id) || 'sent';

                return (
                  <div
                    key={message.id}
                    className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} group`}
                  >
                    <div className={`flex items-end space-x-2 max-w-xs lg:max-w-md ${isOwnMessage ? 'flex-row-reverse space-x-reverse' : ''}`}>
                      {showAvatar && !isOwnMessage && (
                        <img
                          src={message.sender.profileImage}
                          alt={message.sender.username}
                          className="h-6 w-6 rounded-full object-cover"
                        />
                      )}
                      
                      <div className={`relative ${!showAvatar && !isOwnMessage ? 'ml-8' : ''}`}>
                        <div
                          className={`px-4 py-2 rounded-2xl ${
                            isOwnMessage
                              ? 'bg-purple-600 text-white'
                              : 'bg-gray-100 text-gray-900'
                          }`}
                        >
                          {message.message_type === 'text' && (
                            <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                          )}
                          
                          {message.message_type === 'image' && message.media_url && (
                            <div className="space-y-2">
                              <img
                                src={message.media_url}
                                alt="Shared image"
                                className="max-w-full rounded-lg"
                              />
                              {message.content && (
                                <p className="text-sm">{message.content}</p>
                              )}
                            </div>
                          )}
                          
                          {message.message_type === 'post_share' && message.shared_post && (
                            <div className="space-y-2">
                              <div className="bg-white bg-opacity-20 rounded-lg p-2">
                                <p className="text-xs opacity-75">Shared a post</p>
                                <p className="text-sm">{message.shared_post.caption}</p>
                              </div>
                              {message.content && (
                                <p className="text-sm">{message.content}</p>
                              )}
                            </div>
                          )}
                        </div>
                        
                        <div className={`flex items-center mt-1 space-x-2 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                          <span className="text-xs text-gray-500">
                            {formatMessageTime(message.created_at)}
                          </span>
                          
                          {isOwnMessage && (
                            <>
                              <MessageStatus status={messageStatus} />
                              <button
                                onClick={() => handleDeleteMessage(message.id)}
                                className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p className="text-lg font-medium mb-2">Start the conversation</p>
                <p className="text-sm">Send a message to {otherParticipant?.username}</p>
              </div>
            )}

            {/* Typing Indicator */}
            {typingUsers.length > 0 && (
              <TypingIndicator users={typingUsers} className="ml-8" />
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Message Input */}
          <form onSubmit={handleSendMessage} className="border-t border-gray-200 p-4 bg-white">
            <div className="flex items-center space-x-3">
              <Button variant="ghost" size="sm" type="button">
                <Image className="h-5 w-5" />
              </Button>
              
              <div className="flex-1 flex items-center space-x-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={handleInputChange}
                  placeholder="Type a message..."
                  className="flex-1 text-sm border border-gray-300 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  disabled={sending}
                />
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  disabled={!newMessage.trim() || sending}
                  isLoading={sending}
                  className="rounded-full"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Debug Panel */}
      {showDebugPanel && (
        <RealtimeDebugPanel
          conversationId={conversationId}
          onClose={() => setShowDebugPanel(false)}
        />
      )}
    </div>
  );
};